import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { moonToday } from "./MoonPhase";
import { api, useAuth, withRefresh } from "@/lib/auth";

export const DEFAULTS = {
  full: {
    heading: "The moon is full. Pause with the temple for *three minutes.*",
    steps: [
      "Dim the lights. If you can, step outside or sit by a window and let the moonlight find your skin.",
      "Rest one hand on your heart and one on your belly. Breathe in for four, out for six — seven slow breaths.",
      "Ask your body, softly: what is ready to be released? What is ready to be celebrated?",
      "Close with a whisper: “I am full. I am whole. I am home.”",
    ],
    farewell: "Blessed Be",
  },
  new: {
    heading: "The moon is new. Plant one *intention* in the dark.",
    steps: [
      "Sit somewhere quiet and let the room be dim, like the sky tonight.",
      "Rest both hands low on your belly. Breathe slowly until your body feels soft and here.",
      "Ask: what do I want to grow in my body, my pleasure and my life this cycle?",
      "Choose one intention. Say it aloud, simply, as if it is already true.",
      "Write it down and keep it close. Return to it at the full moon.",
    ],
    farewell: "So it is",
  },
};
const LABEL = { full: "Tonight · Full Moon", new: "Tonight · New Moon" };
const SYNODIC = 29.530588853;

export const Gold = ({ text }) =>
  (text ?? "").split(/(\*[^*]+\*)/g).map((p, i) => (p.length > 2 && p.startsWith("*") && p.endsWith("*") ? <em key={i} className="text-[#D9B47A]">{p.slice(1, -1)}</em> : <span key={i}>{p}</span>));

const previewKind = () => {
  const q = new URLSearchParams(window.location.search);
  return q.get("fullmoon") === "preview" ? "full" : q.get("newmoon") === "preview" ? "new" : null;
};

export const moonTonight = () => {
  const preview = previewKind();
  if (preview) return preview;
  const { age } = moonToday();
  if (age > 13.8 && age < 15.8) return "full";
  if (age < 1 || age > SYNODIC - 1) return "new";
  return null;
};
export const cycleKey = (kind) => `tt-${kind}moon-${Math.floor((Date.now() / 86400000 + (kind === "new" ? SYNODIC / 2 : 0)) / SYNODIC)}`;

const Orb = ({ kind }) =>
  kind === "full" ? (
    <span className="w-9 h-9 rounded-full bg-[#F2E8DF] shadow-[0_0_30px_rgba(242,232,223,0.45)]" />
  ) : (
    <span className="w-9 h-9 rounded-full bg-[#120C0A] border border-[#D9B47A]/50 shadow-[inset_-6px_-4px_12px_rgba(217,180,122,0.25)]" />
  );

export const IntentionKeeper = ({ onSaved, tid = "intention", label = "Your intention" }) => {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    withRefresh(() => api.get("/intentions/current"))
      .then(({ data }) => {
        if (!alive || !data?.text) return;
        setText(data.text);
        setSaved(data.text);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await withRefresh(() => api.post("/intentions", { text }));
      setSaved(data?.text ?? text);
      onSaved?.();
    } catch {
      setSaved("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`${tid}-keeper`} className="mt-5 border-t border-[#D9B47A]/15 pt-5">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">{label}</p>
      <textarea data-testid={`${tid}-input`} rows={2} maxLength={280} value={text} onChange={(e) => setText(e.target.value)} placeholder="This cycle, I am growing…" className="mt-2 w-full bg-transparent border border-[#D9B47A]/25 p-3 text-[15px] placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors resize-none" />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span data-testid={`${tid}-status`} className="text-[12px] text-[#A8998C]">{saved && saved === text.trim() ? "Kept safe. It will return to you at the full moon." : "Only you will see this."}</span>
        <button data-testid={`${tid}-save`} disabled={busy || text.trim().length < 3 || saved === text.trim()} onClick={save} className="shrink-0 rounded-full border border-[#D9B47A]/50 px-4 py-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors disabled:opacity-50">Keep it</button>
      </div>
    </div>
  );
};

const IntentionReturn = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    withRefresh(() => api.get("/intentions/current"))
      .then(({ data: d }) => alive && setData(d ?? null))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  if (!data?.text) return null;
  return (
    <div data-testid="intention-return" className="mt-5 border-l-2 border-[#C2414F] pl-4">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">At the new moon, you planted</p>
      <p data-testid="intention-return-text" className="mt-2 font-display italic text-xl text-[#F2E8DF]">“{data.text}”</p>
      <p className="mt-2 text-[13px] text-[#A8998C]">Tonight, notice how it has grown.</p>
    </div>
  );
};

export default function MoonRitual() {
  const { user } = useAuth();
  const [kind, setKind] = useState(null);
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(null);
  useEffect(() => {
    // The card no longer pops up on its own; visitors open it from the moon banner. Admin previews still show it.
    const k = previewKind();
    if (!k) return undefined;
    const t = setTimeout(() => window.dispatchEvent(new CustomEvent("tt:moon-ritual", { detail: k })), 4000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const summon = (ev) => {
      const k = ev.detail;
      if (!DEFAULTS[k]) return;
      setP((cur) => cur ?? DEFAULTS[k]);
      setKind(k);
      setOpen(true);
      if (!process.env.REACT_APP_BACKEND_URL) return;
      api.get(`/moon-practice/${k}`)
        .then(({ data }) => Array.isArray(data?.steps) && data.steps.length && setP({ heading: data.heading ?? DEFAULTS[k].heading, steps: data.steps, farewell: data.farewell ?? DEFAULTS[k].farewell }))
        .catch(() => {});
    };
    window.addEventListener("tt:moon-ritual", summon);
    return () => window.removeEventListener("tt:moon-ritual", summon);
  }, []);
  const close = () => {
    localStorage.setItem(cycleKey(kind), "1");
    setKind(null);
  };
  return (
    <AnimatePresence>
      {kind && p && (
        <motion.aside
          data-testid="moon-ritual"
          data-moon={kind}
          aria-label={kind === "full" ? "Full moon practice" : "New moon practice"}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          data-lenis-prevent
          className="fixed z-[70] left-4 right-4 bottom-4 sm:left-6 sm:right-auto sm:bottom-6 sm:w-[380px] max-h-[80vh] overflow-y-auto border border-[#D9B47A]/30 bg-[#0D0908]/95 backdrop-blur-xl p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]"
        >
          <button data-testid="moon-ritual-close" onClick={close} aria-label="Close" className="absolute top-4 right-4 text-[#A8998C] hover:text-[#F2E8DF] transition-colors"><X className="w-4 h-4" /></button>
          <div className="flex items-center gap-3">
            <Orb kind={kind} />
            <p className="font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#D9B47A]">{LABEL[kind]}</p>
          </div>
          <p data-testid="moon-ritual-heading" className="mt-4 font-display text-2xl leading-snug"><Gold text={p.heading} /></p>
          {open ? (
            <ol data-testid="moon-ritual-steps" className="mt-5 space-y-3">
              {p.steps.map((s, i) => (
                <motion.li key={`${i}-${s}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.12 }} className="flex gap-3 text-sm leading-relaxed text-[#DCCFC3]">
                  <span className="font-tt-mono text-[12px] text-[#C2414F] pt-0.5">{String(i + 1).padStart(2, "0")}</span><span><Gold text={s} /></span>
                </motion.li>
              ))}
            </ol>
          ) : null}
          {user?.initiate && (kind === "new" ? <IntentionKeeper /> : <IntentionReturn />)}
          <div className="mt-6 flex gap-3">
            {!open && <button data-testid="moon-ritual-begin" onClick={() => setOpen(true)} className="rounded-full bg-[#D9B47A] text-[#0A0706] px-5 py-2.5 font-tt-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors">Begin the practice</button>}
            <button data-testid="moon-ritual-dismiss" onClick={close} className="rounded-full border border-[#F2E8DF]/25 px-5 py-2.5 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#CFC2B6] hover:border-[#D9B47A] transition-colors">{open ? p.farewell : "Not tonight"}</button>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
